import { useState } from 'react';
import { ConfirmDialog } from 'app/javascript/components/shared/ConfirmDialog';
import { Button } from 'app/javascript/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/javascript/components/ui/select';
import { useGrantPlan, useRevokeGrant } from 'app/javascript/queries/useAdmin';
import { usePlatformPlans } from 'app/javascript/queries/usePlatformPlans';

// Channels that bill the academy for real. The backend refuses a grant over
// any of them, so the button is not offered either.
const PAID_CHANNELS = ['site', 'app_store', 'play_store'];

// A paid plan granted for free: one select and one button, next to the plan
// it changes. Granting is additive, so it goes straight through; revoking
// takes the academy back to the free plan, so it asks first.
export default function PlanGrantControls({
  slug,
  managedBy,
}: {
  slug: string;
  managedBy: string | null;
}) {
  const { data: plans } = usePlatformPlans();
  const grant = useGrantPlan(slug);
  const revoke = useRevokeGrant(slug);
  const [planId, setPlanId] = useState('');

  const paidPlans = (plans ?? []).filter((plan) => plan.active && plan.price_cents > 0);
  const error = grant.error ?? revoke.error;

  if (managedBy && PAID_CHANNELS.includes(managedBy)) {
    return (
      <p className="text-xs text-muted-foreground">
        Assinatura paga vigente — concessão indisponível até ela terminar.
      </p>
    );
  }

  if (managedBy === 'granted') {
    return (
      <div className="space-y-2">
        <ConfirmDialog
          trigger={
            <Button variant="destructive" size="sm" disabled={revoke.isPending}>
              {revoke.isPending ? 'Revogando…' : 'Revogar concessão'}
            </Button>
          }
          title="Revogar a concessão?"
          description="A academia volta ao plano grátis imediatamente e perde os recursos do plano pago."
          confirmLabel="Revogar concessão"
          onConfirm={() => revoke.mutate()}
        />
        {error && <p className="text-xs text-destructive">{error.message}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={planId} onValueChange={setPlanId}>
          <SelectTrigger className="h-8 w-44 text-xs">
            <SelectValue placeholder="Plano a conceder" />
          </SelectTrigger>
          <SelectContent>
            {paidPlans.map((plan) => (
              <SelectItem key={plan.id} value={String(plan.id)}>
                {plan.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          disabled={!planId || grant.isPending}
          onClick={() => grant.mutate(Number(planId))}
        >
          {grant.isPending ? 'Concedendo…' : 'Conceder plano'}
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error.message}</p>}
    </div>
  );
}
